import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CategoriesAPI } from "@/admin/api";
import { useAdmin } from "@/admin/store";
import { BtnGhost, BtnPrimary, Confirm, Field, Modal, PageHeader, SearchInput, StatusPill, inputCls } from "@/components/admin/ui";

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "");

const Categories = () => {
  const { log, user } = useAdmin();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [form, setForm] = useState({
    categoryName: "",
    slug: "",
    shortName: "",
    sublabel: "",
    displayOrder: 1,
  });

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: CategoriesAPI.getAll,
  });

  const createMutation = useMutation({
    mutationFn: CategoriesAPI.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["categories"]);
      log("Category added", data.categoryName);
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => CategoriesAPI.update(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(["categories"]);
      log("Category updated", variables.payload.categoryName);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: CategoriesAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(["categories"]);
      setConfirmDel(null);
    },
  });

  const openNew = () => {
    const maxOrder = categories.reduce((max, c) => Math.max(max, c.displayOrder || 0), 0);
    setForm({
      categoryName: "",
      slug: "",
      shortName: "",
      sublabel: "",
      displayOrder: maxOrder + 1,
    });
    setSlugManuallyEdited(false);
    setEditing("new");
  };

  const openEdit = (c) => {
    setForm({
      categoryName: c.categoryName || "",
      slug: c.slug || "",
      shortName: c.shortName || "",
      sublabel: c.sublabel || "",
      displayOrder: c.displayOrder || 1,
    });
    setSlugManuallyEdited(true);
    setEditing(c.categoryId);
  };

  const handleNameChange = (val) => {
    setForm((prev) => ({
      ...prev,
      categoryName: val,
      slug: slugManuallyEdited ? prev.slug : slugify(val),
      shortName: prev.shortName === prev.categoryName || !prev.shortName ? val : prev.shortName,
    }));
  };

  useEffect(() => {
    if (location.state?.add) {
      openNew();
    }
  }, [location.state]);

  const rows = categories.filter((c) =>
    (c.categoryName || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.slug || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.shortName || "").toLowerCase().includes(q.toLowerCase())
  );

  const save = () => {
    if (!form.categoryName.trim()) return;
    const payload = {
      categoryName: form.categoryName.trim(),
      slug: form.slug.trim() || slugify(form.categoryName),
      shortName: form.shortName.trim() || form.categoryName.trim(),
      sublabel: form.sublabel.trim() || null,
      displayOrder: Number(form.displayOrder) || 1,
    };

    if (editing === "new") {
      createMutation.mutate({ ...payload, createdBy: user?.userId || 1 });
    } else {
      updateMutation.mutate({ id: editing, payload: { ...payload, isDeleted: false } });
    }
  };

  if (isLoading) return <div className="p-8 text-cream/50">Loading categories...</div>;

  return (
    <div data-testid="admin-categories">
      <PageHeader title="CATEGORIES" sub="The shelves of the kitchen ledger.">
        <SearchInput value={q} onChange={setQ} placeholder="Search categories" />
        <BtnPrimary data-testid="add-category-btn" onClick={openNew}>
          + Add Category
        </BtnPrimary>
      </PageHeader>

      <div className="border-t border-cream/10">
        <div className="hidden grid-cols-12 gap-4 border-b border-cream/10 py-3 font-body text-[9px] tracking-[0.3em] uppercase text-cream/40 md:grid">
          <span className="col-span-2">Category ID</span>
          <span className="col-span-3">Category Name & Details</span>
          <span className="col-span-2">Menu Items</span>
          <span className="col-span-2">Created By</span>
          <span className="col-span-1">Status</span>
          <span className="col-span-2 text-right">Actions</span>
        </div>
        {rows.map((c) => (
          <div key={c.categoryId} data-testid={`category-row-${c.categoryId}`} className="grid grid-cols-2 gap-3 border-b border-cream/10 py-4 md:grid-cols-12 md:items-center md:gap-4">
            <div className="font-body text-xs text-cream/40 md:col-span-2">
              <span className="text-cream/70 font-mono">C-{String(c.categoryId).padStart(2, "0")}</span>
              <span className="block text-[10px] text-cream/35">Seq #{c.displayOrder}</span>
            </div>

            <div className="flex flex-col md:col-span-3">
              {c.sublabel && (
                <span className="font-body text-[8px] tracking-[0.25em] uppercase text-brass/85 font-medium">
                  {c.sublabel}
                </span>
              )}
              <span className="font-display text-lg text-cream leading-tight">{c.categoryName}</span>
              <div className="font-body text-[10px] text-cream/40 flex items-center gap-1.5 mt-0.5">
                {c.shortName && <span className="text-cream/60">{c.shortName}</span>}
                {c.shortName && c.slug && <span>·</span>}
                {c.slug && <span className="font-mono text-[9px] text-cream/35">/{c.slug}</span>}
              </div>
            </div>

            <span className="font-body text-xs font-light text-cream/60 md:col-span-2">{c.itemCount ?? 0} {c.itemCount === 1 ? "item" : "items"}</span>
            <span className="font-body text-xs font-light text-cream/40 md:col-span-2">{c.createdByName || (c.createdBy ? `User ${c.createdBy}` : "System")}</span>
            <span className="md:col-span-1">
              <StatusPill tone={c.isDeleted ? "terra" : "leaf"}>{c.isDeleted ? "Deleted" : "Active"}</StatusPill>
            </span>
            <div className="col-span-2 flex gap-3 md:col-span-2 md:justify-end">
              <button data-testid={`category-edit-${c.categoryId}`} onClick={() => openEdit(c)} className="font-body text-[10px] tracking-[0.2em] uppercase text-brass/80 hover:text-brass">Edit</button>
              {!c.isDeleted ? (
                <button data-testid={`category-delete-${c.categoryId}`} onClick={() => setConfirmDel(c)} className="font-body text-[10px] tracking-[0.2em] uppercase text-terra/80 hover:text-terra">Delete</button>
              ) : (
                <button onClick={() => updateMutation.mutate({ id: c.categoryId, payload: { ...c, isDeleted: false } })} className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/60 hover:text-brass">Restore</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal wide title={editing === "new" ? "ADD CATEGORY" : "EDIT CATEGORY"} onClose={() => setEditing(null)}>
          <div className="space-y-4">
            <Field label="Category Name">
              <input
                data-testid="category-form-name"
                className={inputCls}
                value={form.categoryName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Coastal Specialties"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Slug (URL & Menu Anchors)">
                <input
                  data-testid="category-form-slug"
                  className={inputCls}
                  value={form.slug}
                  onChange={(e) => {
                    setSlugManuallyEdited(true);
                    setForm({ ...form, slug: e.target.value });
                  }}
                  placeholder="e.g. coastal-specialties"
                />
              </Field>

              <Field label="Short Name (Navigation Tabs)">
                <input
                  data-testid="category-form-shortname"
                  className={inputCls}
                  value={form.shortName}
                  onChange={(e) => setForm({ ...form, shortName: e.target.value })}
                  placeholder="e.g. Coastal"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Sub-label (Eyebrow Tagline)">
                <input
                  data-testid="category-form-sublabel"
                  className={inputCls}
                  value={form.sublabel}
                  onChange={(e) => setForm({ ...form, sublabel: e.target.value })}
                  placeholder="e.g. COASTAL CATCH"
                />
              </Field>

              <Field label="Display Order (Sequence #)">
                <input
                  data-testid="category-form-order"
                  type="number"
                  min="1"
                  className={inputCls}
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                  placeholder="1"
                />
              </Field>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <BtnGhost onClick={() => setEditing(null)}>Cancel</BtnGhost>
            <BtnPrimary data-testid="category-form-save" onClick={save} disabled={createMutation.isPending || updateMutation.isPending}>
              {editing === "new" ? "Add Category" : "Save Changes"}
            </BtnPrimary>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Confirm
          testId="category-delete-confirm"
          title="REMOVE THIS CATEGORY?"
          body={`“${confirmDel.categoryName}” will be removed from the shelf.`}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => {
            deleteMutation.mutate(confirmDel.categoryId);
            log("Category removed", confirmDel.categoryName);
          }}
        />
      )}
    </div>
  );
};

export default Categories;
